import React, { type ComponentPropsWithoutRef, type CSSProperties, forwardRef, type RefObject } from 'react';

import { isNumber } from '../../helpers/number';
import { useRefAndForward } from '../../hooks';
import { useTheme } from '../../theme';
import { Portal } from '../Portal';

import { useMaxWidth, usePositionX, usePositionY } from './helpers';
import { Styled } from './styles';
import type { OverlayVariant } from './types';

export { overlayVariants } from './types';
export type { OverlayVariant } from './types';

type OverlayProps = {
  targetRef: RefObject<HTMLElement | null>;
  variant: OverlayVariant;
  isOpened: boolean;
  maxHeight?: CSSProperties['maxHeight'];
  disablePortalForOptions?: boolean;
  shouldFitContentWidth?: boolean;
};
type DivProps = Omit<ComponentPropsWithoutRef<'div'>, keyof OverlayProps>;

type Props = DivProps & OverlayProps;

export const Overlay = forwardRef<HTMLDivElement, Props>(
  ({ variant, targetRef, isOpened, maxHeight, disablePortalForOptions, shouldFitContentWidth, ...props }, ref) => {
    const overlayRef = useRefAndForward<HTMLDivElement | null>(null, ref);
    const theme = useTheme();
    const { overlaySpaceToTarget } = theme.designTokens;
    const maxWidth = useMaxWidth({ targetRef, isOpened, variant, overlaySpaceToTarget });
    const positionX = usePositionX({
      overlayRef,
      targetRef,
      isOpened,
      variant,
      overlaySpaceToTarget,
    });
    const positionY = usePositionY({ overlayRef, targetRef, isOpened, overlaySpaceToTarget });
    const isReadyForDisplay = isNumber(maxWidth) && isNumber(positionX) && isNumber(positionY);
    const isDisplayed = isOpened && isReadyForDisplay;

    const overlayProps = {
      'aria-hidden': !isDisplayed,
      maxHeight: maxHeight ?? null,
      ref: overlayRef,
      isDisplayed,
      positionX,
      positionY,
      ...(shouldFitContentWidth ? { maxWidth: 'fit-content' } : { maxWidth: `${maxWidth}px` }),
      ...(shouldFitContentWidth && maxWidth ? { minWidth: `${maxWidth}px` } : {}),
      variant,
      disablePortalForOptions,
      ...props,
    };

    if (disablePortalForOptions) {
      return <Styled.Container {...overlayProps} />;
    }

    return (
      <Portal>
        <Styled.Container {...overlayProps} />
      </Portal>
    );
  },
);
Overlay.displayName = 'Overlay';
